<template>
  <div
      v-if="event && !singlePeriod && initialised"
  >
    <booking-summary
        :event="event"
        :booking="booking"
        class="mb-3"
    ></booking-summary>

    <HtmlText tag="h2" :text="$t('chooseYourLocker')" class="mt-3 mb-4"></HtmlText>

    <p v-if="visiblePeriods.length === 0" class="pt-2">
      {{ $t('currentlyNoLockersAvailable') }}
    </p>

    <ul class="zone-list list-group pt-2">
      <li class="list-group-item d-flex justify-content-between align-items-center"
          v-for="period in visiblePeriods"
          v-bind:key="period.id"
          @click="setPeriod(period)"
      >
          <span class="d-flex flex-column justify-content-start align-items-start">
            {{ getPeriodText(period, 'name') }}
            <small class="text-muted" v-if="getPeriodText(period, 'description')">
              {{ getPeriodText(period, 'description') }}
            </small>
          </span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-chevron-right" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"/></svg>
      </li>
    </ul>

    <div
        v-if="booking.can_select_zone"
        class="d-flex justify-content-between align-items-center pt-5"
    >
      <button
          type="button"
          class="btn btn-link btn-sm ms-n2"
          @click="resetZone"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-left-short" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5"/></svg>
        <span class="ms-1">{{ $t('back') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import BookingSummary from '../components/BookingSummary';
import HtmlText from '../components/HtmlText';
import translationMixin from '../mixins/translationMixin';
import posMixin from '../mixins/posMixin';
import moment from 'moment';

const refreshTimeout = 60; // Refresh timeout in seconds

export default {
  mixins: [translationMixin, posMixin],

  components: {
    BookingSummary,
    HtmlText
  },

  props: [
    'event',
    'booking'
  ],

  data() {
    return {
      visiblePeriods: [],
      initialised: false,
      componentBooking: null,
      refreshTimer: null,
      isQR: false,
    }
  },

  computed: {
    /**
     * Just a single period?
     */
    singlePeriod() {
      return this.visiblePeriods.length === 1;
    }
  },

  watch: {
    '$route': 'init',
    event: {
      handler() {
        this.init();
      },
      deep: true
    }
  },

  /**
   * Initialise component and start refresh.
   */
  created() {
    this.init();
    this.startRefreshTimer();
  },

  /**
   * Stop refresh timer.
   */
  beforeDestroy() {
    this.stopRefreshTimer();
  },

  methods: {
    /**
     * Initialise component, determine visible periods and set booking.
     */
    init() {
      this.isQR = this.$route.query.qr;

      // Can a zone be selected?
      if ((this.event.isolate_zones || this.event.show_zones) && this.booking.preferred_zone === null) {
        this.booking.can_select_zone = true;
        this.$router.push('/event/' + this.event.code + '/zones');
        return;
      }

      this.componentBooking = this.booking;
      this.determineVisiblePeriods();
      this.initialised = true;
    },

    /**
     * Determine visible periods of event.
     *
     * - For POS and QR code only the active periods
     * - Otherwise all future periods
     *
     * Just a single period? Straight to period.
     */
    determineVisiblePeriods() {
      if (this.inPosMode() || this.isQR) {
        // Check active periods
        this.visiblePeriods = this.event.periods.filter(period => {
          return moment(period.assign_at).isBefore() && moment(period.end).isAfter();
        });

        // Show first period if no active periods.
        if (this.isQR && this.visiblePeriods.length === 0 && this.event.periods.length > 0) {
          this.visiblePeriods = [this.event.periods[0]];
        }
      } else {
        // All periods
        this.visiblePeriods = this.event.periods;
      }

      // Just one period? Directly to period
      if (this.singlePeriod) {
        this.setPeriod(this.visiblePeriods[0]);
      }
    },

    /**
     * Set period and navigate to next step.
     *
     * @param {Object} period
     */
    setPeriod(period) {
      this.componentBooking.period = period;
      if (+this.componentBooking.period_id !== +period.id) {
        this.componentBooking.period_id = period.id;
        this.componentBooking.setLinesForLockerSizes(this.event.locker_sizes, period.prices);
      }

      this.$emit('ready', this.componentBooking, false);
      this.$router.push('/event/' + this.event.code + '/lockers');
    },

    /**
     * Reset selected zone and navigate back to zone selection.
     */
    resetZone() {
      this.componentBooking.preferred_zone = null;
      this.$router.push('/event/' + this.event.code + '/zones');
    },

    /**
     * Start timer for refreshing visible periods.
     */
    startRefreshTimer() {
      this.refreshTimer = setInterval(this.determineVisiblePeriods, refreshTimeout * 1000);
    },

    /**
     * Stop timer for refreshing visible periods.
     */
    stopRefreshTimer() {
      if (this.refreshTimer === null) {
        return;
      }

      clearInterval(this.refreshTimer);

      this.refreshTimer = null;
    }
  }
}
</script>
